import './searchBar.scss';
import React, { useEffect, useState } from 'react';
import {
    assoc,
    curry,
    defaultTo,
    find,
    isEmpty,
    map,
    pipe,
    prop,
    propEq
} from 'ramda';
import { SearchFilter } from '../searchFilter/SearchFilter';
import classNames from 'classnames';
import {  userStore, searchStore, generalStore } from '../../store';
import FilterTags from '../filterTags/FilterTags';
import Helpers from '../../js/helpers';
import Config, { isCupra } from '../../js/config/_global';
import IconButton from '../iconButton/IconButton';
import { useTranslation } from 'react-i18next';
import SortToggle from '../sortToggle/SortToggle';
import Dropdown from '../dropdown/Dropdown';
import {
    SORT_MEDIA_OPTIONS,
    DEFAULT_MEDIA_ORDER
} from '../../js/_constants';
import trashIconPath from '@icons/trash-without-handle.svg';
import { isNotEmpty } from '../../js/fpUtils';

const getCount = (arr: any, key: any) =>
    pipe(
        find(propEq(key, 'key')),
        prop('count'),
        defaultTo(0)
    )(arr);

const attachAggregationCountFrom = curry((arr, item) =>
    assoc('count', getCount(arr, item.key), item)
);

const SearchBar = props => {
    const { t } = useTranslation();
    const { details } = userStore();
    const { doSearch,
            getFilterPreview,
            resetSearch,
            setIsFromSearchInput,
            search,
            totalItems,
            isSearching,
            aggregations,
            isFromSearchInput,
            previewSearch,
            allCarModels,
            allCarTypes 
        } = searchStore();

    const {loading} = generalStore();

    const { openAssetUpload } = props;

    const queryParameter = Helpers.getQueryParameter();

    const sortOptions = SORT_MEDIA_OPTIONS.map(option => t(option.title));

    const [filterTags, setFilterTags] = useState<any>([]);

    const [filters, setFilters] = useState([]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const [savedFilters, setSavedFilters] = useState({
        carTypes: search.carTypes,
        tags: search.tags,
        carModels: search.carModels,
        modelYears: search.modelYears,
        kvps: search.kvps
    });

    const [expiredDate, setExpiredDate] = useState(
        queryParameter.expiredDate ? queryParameter.expiredDate : ''
    );

    const [managedMediaOnly, setManagedMediaOnly] = useState(
        queryParameter.managedMediaOnly ? true : ''
    );

    const [mediaType, setType] = useState(
        queryParameter.type ? queryParameter.type : ''
    );

    const [myMediaIsSelected, setMyMediaIsSelected] = useState(managedMediaOnly ? true : false);

    const [isExpiredMediaChecked, setIsExpiredMediaChecked] = useState<any>(
        queryParameter.expiredDate || false
    );

    const [isManagedMediaOnlyChecked, setIsManagedMediaOnlyChecked] = useState<any>(
        queryParameter.managedMediaOnly ? true : ''
    );

    const [preselection, setPreselection] = useState(0);

    const [currentAggregations, setCurrentAggregations] = useState(aggregations);

    useEffect(() => {
        let preselection = 0;

        if (queryParameter['order[created]']) {
            preselection = SORT_MEDIA_OPTIONS.findIndex(
                option =>
                    option.sort === 'order[created]' &&
                    option.order === queryParameter['order[created]']
        )} else if (queryParameter['order[relevance]']) {
            preselection = SORT_MEDIA_OPTIONS.findIndex(
                option =>
                    option.sort === 'order[relevance]' &&
                    option.order === queryParameter['order[relevance]']
        )}
        setPreselection(preselection);
    }, [queryParameter]);

    useEffect(() => {
        if (!isFromSearchInput) {
            const search = {   
                type:  queryParameter.type ? queryParameter.type : '',
                expiredDate:  queryParameter.expiredDate ? queryParameter.expiredDate : '',
                managedMediaOnly:  queryParameter.managedMediaOnly ? true : '',
                page: queryParameter.page ? queryParameter.page : 1,
                query: queryParameter.query ? queryParameter.query : '',
                title:  queryParameter.title ? queryParameter.title : '',
                tags:  queryParameter.tag ? queryParameter.tag.split(',') : [],
                'order[created]': queryParameter['order[relevance]'] ? '' : queryParameter['order[created]'],
                'order[relevance]': queryParameter['order[created]'] ? '' : queryParameter['order[relevance]'],
                ...(!queryParameter['order[relevance]'] && !queryParameter['order[created]'] && {'order[created]': DEFAULT_MEDIA_ORDER})
            };
            executeSearch(search);
        }
    }, []);

    useEffect(() => {
        const { filterTags, savedFilters } = buildFilterTags();
        setSavedFilters(savedFilters);
        setFilterTags(filterTags);

        return () => {
            resetSearch();
        };
    }, []);

    useEffect(() => {
        // using isSearching assured that searching is done, cuz some times aggregations keeps the same even with deferent search params
        // and filter will not set
        if (isCupra) {
            delete aggregations.carTypes;
            delete aggregations.carModels;
        }
        setCurrentAggregations(aggregations);
        setFilter();
    }, [isSearching]);

    useEffect(() => {
        if (isFromSearchInput) {
            const search = {   
                        page: 1,
                        query: queryParameter.query ? queryParameter.query : '',
                        'order[relevance]': DEFAULT_MEDIA_ORDER,
                        'order[created]': '',
                        tags:  queryParameter.tag ? queryParameter.tag.split(',') : [],
                        title:  queryParameter.title ? queryParameter.title : '',
                    };
            executeSearch(search, true);
      }
    }, [isFromSearchInput]);

    const updateFilter = async (selectedFilters, event) => {
        setIsLoadingPreview(true);
        /**
         * When model years sliders is slid, we dont want to update the preview filters.
         * Model years will be applied only when apply filters has been clicked.
         */
        if (event && event.target && event.target.title === 'awc-slider') {
            setSavedFilters(selectedFilters);
            return setIsLoadingPreview(false);
        }

        await getFilterPreview(selectedFilters);
        setSavedFilters(selectedFilters);
        setIsLoadingPreview(false);
    };

    const executeSearch = async (search, fromSearchInput = false) => {
        await doSearch({
            ...savedFilters,
            ...search
        });
        setIsLoadingPreview(false);
        fromSearchInput && setIsFromSearchInput(false);
    };

    const applyFilters = savedFilters => {
        setIsFilterOpen(false);
        setSavedFilters(savedFilters);
        const search = {   
            expiredDate: expiredDate,
            managedMediaOnly: managedMediaOnly,
            page: 1,
        };
        executeSearch(search);
    };

    const buildFilterTags = () => {
        const filterTags: any[] = [];

        if (search.modelYears.low) {
            filterTags.push({
                key: 'modelYears',
                title: `${search.modelYears.low} - ${search.modelYears.high}`,
                low: search.modelYears.low,
                high: search.modelYears.high
            });
        }

        if (search.expiredDate) {
            filterTags.push({
                key: 'expiredMedia',
                title: t('filter_expired_media')
            });
        }

        if (search.query) {
            filterTags.push({
                key: 'query',
                title: search.query
            });
        }

        if (search.title) {
            filterTags.push({
                key: 'title',
                title: search.title
            });
        }

        const savedFilters = {
            tags: search.tags,
            carTypes: search.carTypes,
            carModels: search.carModels,
            modelYears: search.modelYears,
            kvps: search.kvps
        };

        Object.keys(savedFilters).forEach(key => {
            const filter = savedFilters[key];
            for (let i = 0; i < filter.length; i += 1) {
                filterTags.push({ key, title: filter[i] });
            }
        });

        return { filterTags, savedFilters };
    };

    const setFilter = () => {
        const selectedFilters = savedFilters;

        const selectedModels = selectedFilters.carModels;

        const { filterTags, savedFilters: savedBuildFilters } =
            buildFilterTags();
        const carTypes = isEmpty(selectedModels)
            ? allCarTypes
            : selectedModels
                  .reduce(
                      (acc, currentValue) =>
                          acc.concat(
                            // @ts-ignore
                            allCarModels.find(m => m.model === currentValue)
                                  ?.carTypes
                          ),
                      []
                  )                           
                   // @ts-ignore
                  .map(carType => ({ ...carType, key: carType?.type }));

        /**
         * We nullify filter options if those do not exists.
         * The SearchFilter component only shows options that exists in filterOptions
         */
        setFilters({
            ...aggregations,
            carModels: aggregations && aggregations.carModels
                ? map(attachAggregationCountFrom(aggregations.carModels), allCarModels)
                : null,
            carTypes: aggregations && aggregations.carTypes
                ? map(attachAggregationCountFrom(aggregations.carTypes), carTypes)
                : null
        });
        setFilterTags(filterTags);
        setSavedFilters(savedBuildFilters);
        setCurrentAggregations(aggregations);
    };

    const openFilter = e => {
        e.stopPropagation();
        setIsFilterOpen(!isFilterOpen);
    };

    const closeFilter = () => {
        setIsFilterOpen(false);
    };

    const removeFilter = (name, index, filterKey) => {
        if (filterKey === 'expiredMedia') {
            setFilterTags(filterTags.filter(tag => tag.key !== filterKey));
            setIsExpiredMediaChecked(false);
            setExpiredDate('');
            const search = {   
                expiredDate: '',
            };
            return executeSearch(search);
        }

        if (filterKey === 'query' || filterKey === 'title') {
            setFilterTags(filterTags.filter(tag => tag.key !== filterKey));
        }
        const oldFilters = savedFilters;

        if (filterKey !== 'modelYears' && filterKey !== 'query'&& filterKey !== 'title') {
            const itemIndex = oldFilters[filterKey].indexOf(name);
            oldFilters[filterKey].splice(itemIndex, 1);
        } else {
            oldFilters[filterKey] = { low: '', high: '' };
        }
        setFilters(filters);
        setFilterTags(filterTags);
        setSavedFilters(oldFilters);
        const search = {   
            page: 1,
            query: filterKey === 'query' ? '' : queryParameter.query,
            title: filterKey === 'title' ? '' : queryParameter.title
        };

        executeSearch(search);
    };

    const resetFilter = async () => {
        const savedFilters = {
            kvps: [],
            tags: [],
            carTypes: [],
            carModels: [],
            modelYears: {
                high: '',
                low: ''
            }
        };

        setFilters(filters);
        setType('');
        setFilterTags([]);
        setSavedFilters(savedFilters);
        setIsExpiredMediaChecked(false);
        setIsManagedMediaOnlyChecked(false);
        setManagedMediaOnly('')
        setPreselection(0);
        setExpiredDate('');
        setMyMediaIsSelected(false);

        const search = {  
            kvps: [], 
            type: '',
            expiredDate:'',
            managedMediaOnly: '',
            page: 1,
            query: '',
            title: '',
            'order[created]': DEFAULT_MEDIA_ORDER,
            'order[relevance]': '',
        };
        executeSearch({ 
            ...savedFilters,
            ...search
        });
    };

    const handleChangeSearchType = (value, managedMediaOnly) => {
        setType(value);
        setMyMediaIsSelected(managedMediaOnly) 
        const search = {   
            type: value,
            page: 1,
            managedMediaOnly
        };
        executeSearch(search);
    };

    const handleChangeSortBy = (e, name, sort, index) => {
        setPreselection(index);
        const search = {   
            'order[created]': SORT_MEDIA_OPTIONS[index].sort === 'order[created]' ? SORT_MEDIA_OPTIONS[index].order : '',
            'order[relevance]': SORT_MEDIA_OPTIONS[index].sort === 'order[relevance]' ? SORT_MEDIA_OPTIONS[index].order : '',
        };
        executeSearch(search);
    };

    const handleShowExpiredMediaOnly = () => {
        const date = new Date();

        if (!isExpiredMediaChecked) {
            setExpiredDate(date.toISOString()); // rfc 3339 format
            setIsExpiredMediaChecked(true);
        } else {
            setExpiredDate(''); // rfc 3339 format
            setIsExpiredMediaChecked(false);
        }
    };

    const renderFilters = () => {
        if (isFilterOpen) {
            return (
                <div
                    className={classNames('b-search-filter-container', {
                        'is-loading': isLoadingPreview
                    })}
                    data-testid='filter container'
                >
                    <SearchFilter
                        searchAggregations={currentAggregations}
                        carModels={allCarModels as any}
                        carTypes={allCarTypes  as any}
                        isSearching={isLoadingPreview}
                        onApplyFilters={applyFilters}
                        onChange={updateFilter}
                        onClose={closeFilter}
                        onResetFilters={resetFilter}
                        onShowExpiredMediaOnly={handleShowExpiredMediaOnly}
                        config={{
                            isExpiredMediaTabVisible: true,
                            isExpiredMediaChecked: isExpiredMediaChecked,
                        }}
                        secondaryButtonText={t('cancel')}
                        selectedOptions={previewSearch as any}
                    />
                </div>
            );
        }
        return '';
    };

    const renderFilterTags = () => {
        if (isNotEmpty(filterTags)) {
            return (
                <FilterTags
                    key="filter-tags"
                    tags={filterTags}
                    classes="is-searchbar"
                    onRemoveFilter={removeFilter}
                />
            );
        }

        return '';
    };

    const isTypeSelected = type => {
        return mediaType === type;
    };

    return (
        <div className="b-search-bar">
            <div className="b-search-menu">
                <div className="b-left-cluster">
                    <button
                        className={classNames('button', {
                            active: isTypeSelected('') && !myMediaIsSelected
                        })}
                        onClick={() => handleChangeSearchType('', '')}
                        data-testid="all-media-button"
                    >
                        {t('all_media')}
                    </button>
                    <button
                        className={classNames('button', {
                            active: isTypeSelected(Config.mediaTypes.video) && !myMediaIsSelected
                        })}
                        onClick={() =>
                            handleChangeSearchType(Config.mediaTypes.video, '')
                        }
                        data-testid="videos-button"
                    >
                        {t('videos')}
                    </button>
                    <button
                        className={classNames('button', {
                            active: isTypeSelected(Config.mediaTypes.image) && !myMediaIsSelected
                        })}
                        onClick={() =>
                            handleChangeSearchType(Config.mediaTypes.image, '')
                        }
                        data-testid="images-button"
                    >
                        {t('images')}
                    </button>
                    {details.isAllowedToUpload && <button
                        className={classNames('button', {
                            active: myMediaIsSelected
                        })}
                        onClick={() =>
                            handleChangeSearchType('', true)
                        }
                        data-testid="my-media-button"
                    >
                        {t('my_media')}
                    </button>}
                </div>
                <div className="flex-spacer" />
                <div className="b-right-cluster">
                    {details.isAllowedToUpload && <IconButton
                        icon="upload-s"
                        onClick={openAssetUpload}
                        name='upload-button'
                        size="medium"
                    />}
                    <IconButton
                        icon="filter"
                        onClick={openFilter}
                        size="small"
                        disabled={isEmpty(aggregations)}
                        name='filter-button'
                    />
                    <SortToggle />
                    <Dropdown
                        name="sorting"
                        label={t('sort_by')}
                        options={sortOptions}
                        onSelect={handleChangeSortBy}
                        preselection={preselection}
                    />
                </div>
            </div>

            {renderFilters()}
            {!isSearching && !loading && (
                <div className="search-results-wrapper">
                    {renderFilterTags()}
                    <div className="results-wrapper">
                        <span
                            className={classNames(
                                'results-total',
                                totalItems === 0 ? 'no-results' : '',
                            )}
                            data-testid="media-result-counts"
                        >
                            {totalItems} {t('results')}
                        </span>
                        {isNotEmpty(filterTags) && (
                            <button
                                className="reset-button"
                                onClick={resetFilter}
                            >
                                <img
                                    src={trashIconPath}
                                    className="trash-icon"
                                />
                                {t('results_reset')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchBar;
